import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  CloudUpload as ProvisionIcon,
  Delete as DeleteIcon,
  DeleteForever as TerminateIcon,
  Edit as EditIcon,
  InfoOutlined as DetailsIcon,
  MoreVert as MoreIcon,
  OpenInNew as OpenIcon,
  PlayCircleFilled as StartIcon,
  StopCircle as StopIcon,
  SystemUpdateAlt as DeployIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import SearchableDataTable from '../components/SearchableDataTable';
import '../styles/EntityManagement.css';

const createStatusOptions = ['draft', 'provisioning'];
const editStatusOptions = ['draft', 'active', 'suspended', 'archived'];

const emptySite = {
  companyName: '',
  slug: '',
  status: 'draft',
  setupOwnerUserId: '',
  initialAdminFirstName: '',
  initialAdminLastName: '',
  initialAdminEmail: '',
  metadata: {},
  notes: ''
};

const statusColors = {
  draft: 'default',
  provisioning: 'warning',
  active: 'success',
  suspended: 'error',
  archived: 'default',
  failed: 'error'
};

const formatStatus = (status) => (
  status ? status.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()) : ''
);

const formatStatusOption = (status) => (
  status === 'provisioning' ? 'Provision Now' : formatStatus(status)
);

const normalizeSlug = (value) => (
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
);

const SiteManagement = () => {
  const { site } = useAuth();
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentSite, setCurrentSite] = useState(emptySite);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [provisioningSiteId, setProvisioningSiteId] = useState(null);
  const [refreshingEc2, setRefreshingEc2] = useState(false);
  const [refreshingSiteId, setRefreshingSiteId] = useState(null);
  const [deletingSiteId, setDeletingSiteId] = useState(null);
  const [startingSiteId, setStartingSiteId] = useState(null);
  const [stoppingSiteId, setStoppingSiteId] = useState(null);
  const [terminatingSiteId, setTerminatingSiteId] = useState(null);
  const [deployingSiteId, setDeployingSiteId] = useState(null);
  const [deployingAll, setDeployingAll] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [siteDetails, setSiteDetails] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuSite, setActionMenuSite] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const showNotification = (message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  };

  const loadSites = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/sites');
      setSites(response.data.data || []);
    } catch (error) {
      console.error('Error loading sites:', error);
      showNotification(error.response?.data?.message || 'Error loading sites', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error loading setup owners:', error);
      showNotification(error.response?.data?.message || 'Error loading setup owners', 'error');
    }
  }, []);

  useEffect(() => {
    if (site?.isGlobal) {
      loadSites();
      loadUsers();
    }
  }, [loadSites, loadUsers, site]);

  const handleAdd = () => {
    setEditMode(false);
    setCurrentSite(emptySite);
    setOpenDialog(true);
  };

  const handleEdit = (site) => {
    const initialAdminConfig = site.metadata?.initialAdminConfig || site.metadata?.initialAdmin || {};
    setEditMode(true);
    setCurrentSite({
      companyName: site.companyName || '',
      slug: site.slug || '',
      status: site.status || 'draft',
      setupOwnerUserId: site.setupOwnerUserId || '',
      initialAdminFirstName: initialAdminConfig.firstName || '',
      initialAdminLastName: initialAdminConfig.lastName || '',
      initialAdminEmail: initialAdminConfig.email || '',
      metadata: site.metadata || {},
      notes: site.notes || '',
      id: site.id
    });
    setOpenDialog(true);
  };

  const handleOpenActionMenu = (event, site) => {
    setActionMenuAnchor(event.currentTarget);
    setActionMenuSite(site);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuSite(null);
  };

  const handleActionMenuItem = (handler, site) => {
    handleCloseActionMenu();
    handler(site);
  };

  const handleOpenTenantUrl = (tenantUrl) => {
    if (!tenantUrl) return;
    window.open(tenantUrl, '_blank', 'noopener,noreferrer');
  };

  const handleClose = () => {
    setOpenDialog(false);
    setEditMode(false);
    setCurrentSite(emptySite);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setCurrentSite((site) => ({
      ...site,
      [name]: name === 'slug' ? normalizeSlug(value) : value
    }));
  };

  const handleCompanyNameChange = (event) => {
    const companyName = event.target.value;
    setCurrentSite((site) => ({
      ...site,
      companyName,
      slug: editMode || site.slug ? site.slug : normalizeSlug(companyName)
    }));
  };

  const handleSave = async () => {
    if (!currentSite.companyName.trim()) {
      showNotification('Company name is required', 'error');
      return;
    }

    if (!currentSite.slug.trim()) {
      showNotification('Subdomain is required', 'error');
      return;
    }

    if (currentSite.status === 'provisioning' && !currentSite.initialAdminEmail.trim()) {
      showNotification('Initial admin email is required before provisioning', 'error');
      return;
    }

    try {
      const initialAdminConfig = {
        firstName: currentSite.initialAdminFirstName.trim(),
        lastName: currentSite.initialAdminLastName.trim(),
        email: currentSite.initialAdminEmail.trim().toLowerCase()
      };
      const payload = {
        ...currentSite,
        companyName: currentSite.companyName.trim(),
        slug: normalizeSlug(currentSite.slug),
        metadata: {
          ...(currentSite.metadata || {}),
          initialAdminConfig
        }
      };

      delete payload.initialAdminFirstName;
      delete payload.initialAdminLastName;
      delete payload.initialAdminEmail;

      if (editMode) {
        await api.put(`/sites/${currentSite.id}`, payload);
        showNotification('Site updated successfully', 'success');
      } else {
        const shouldProvision = payload.status === 'provisioning';
        const response = await api.post('/sites', {
          ...payload,
          status: 'draft'
        });

        if (shouldProvision && response.data?.data?.id) {
          await api.post(`/sites/${response.data.data.id}/provision`);
          showNotification('Site created and provisioning queued', 'success');
        } else {
          showNotification('Site created successfully', 'success');
        }
      }

      await loadSites();
      handleClose();
    } catch (error) {
      console.error('Error saving site:', error);
      showNotification(error.response?.data?.message || 'Error saving site', 'error');
    }
  };

  const handleProvision = async (site) => {
    if (!site.metadata?.initialAdminConfig?.email) {
      showNotification('Initial admin email is required before provisioning. Edit the site first.', 'error');
      return;
    }

    const confirmed = window.confirm(`Provision ${site.companyName} as ${site.slug}?`);
    if (!confirmed) return;

    try {
      setProvisioningSiteId(site.id);
      await api.post(`/sites/${site.id}/provision`);
      showNotification('Provisioning job queued', 'success');
      await loadSites();
    } catch (error) {
      console.error('Error provisioning site:', error);
      showNotification(error.response?.data?.message || 'Error queueing provisioning job', 'error');
    } finally {
      setProvisioningSiteId(null);
    }
  };

  const handleRefreshEc2 = async () => {
    try {
      setRefreshingEc2(true);
      await api.post('/sites/refresh-ec2');
      showNotification('EC2 statuses refreshed', 'success');
      await loadSites();
    } catch (error) {
      console.error('Error refreshing EC2 statuses:', error);
      showNotification(error.response?.data?.message || 'Error refreshing EC2 statuses', 'error');
    } finally {
      setRefreshingEc2(false);
    }
  };

  const handleRefreshSiteEc2 = async (site) => {
    try {
      setRefreshingSiteId(site.id);
      await api.post(`/sites/${site.id}/refresh-ec2`);
      showNotification('EC2 status refreshed', 'success');
      await loadSites();
    } catch (error) {
      console.error('Error refreshing site EC2 status:', error);
      showNotification(error.response?.data?.message || 'Error refreshing EC2 status', 'error');
    } finally {
      setRefreshingSiteId(null);
    }
  };

  const handleDeleteArchivedSite = async (site) => {
    const ec2State = site.metadata?.ec2State || 'unknown';
    const confirmed = window.confirm(
      `Delete the archived site row for ${site.companyName}? EC2 state is ${ec2State}.`
    );

    if (!confirmed) return;

    try {
      setDeletingSiteId(site.id);
      await api.delete(`/sites/${site.id}`);
      showNotification('Archived site row deleted', 'success');
      await loadSites();
    } catch (error) {
      console.error('Error deleting archived site:', error);
      showNotification(error.response?.data?.message || 'Error deleting archived site', 'error');
    } finally {
      setDeletingSiteId(null);
    }
  };

  const handleStopEc2 = async (site) => {
    const confirmed = window.confirm(`Stop the EC2 instance for ${site.companyName}?`);
    if (!confirmed) return;

    try {
      setStoppingSiteId(site.id);
      await api.post(`/sites/${site.id}/stop-ec2`);
      showNotification('EC2 stop requested', 'success');
      await loadSites();
    } catch (error) {
      console.error('Error stopping EC2 instance:', error);
      showNotification(error.response?.data?.message || 'Error stopping EC2 instance', 'error');
    } finally {
      setStoppingSiteId(null);
    }
  };

  const handleStartEc2 = async (site) => {
    const confirmed = window.confirm(`Start the EC2 instance for ${site.companyName}?`);
    if (!confirmed) return;

    try {
      setStartingSiteId(site.id);
      await api.post(`/sites/${site.id}/start-ec2`);
      showNotification('EC2 start requested', 'success');
      await loadSites();
    } catch (error) {
      console.error('Error starting EC2 instance:', error);
      showNotification(error.response?.data?.message || 'Error starting EC2 instance', 'error');
    } finally {
      setStartingSiteId(null);
    }
  };

  const handleTerminateEc2 = async (site) => {
    const confirmed = window.confirm(
      `Terminate the EC2 instance for ${site.companyName}? This cannot be undone.`
    );

    if (!confirmed) return;

    try {
      setTerminatingSiteId(site.id);
      await api.post(`/sites/${site.id}/terminate-ec2`);
      showNotification('EC2 termination requested', 'success');
      await loadSites();
    } catch (error) {
      console.error('Error terminating EC2 instance:', error);
      showNotification(error.response?.data?.message || 'Error terminating EC2 instance', 'error');
    } finally {
      setTerminatingSiteId(null);
    }
  };

  const handleOpenDetails = async (site) => {
    try {
      setDetailsOpen(true);
      setDetailsLoading(true);
      setSiteDetails(null);
      const response = await api.get(`/sites/${site.id}/details`);
      setSiteDetails(response.data.data);
    } catch (error) {
      console.error('Error loading site details:', error);
      showNotification(error.response?.data?.message || 'Error loading site details', 'error');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSiteDetails(null);
  };

  const handleDeploySite = async (site) => {
    const confirmed = window.confirm(`Deploy latest ${site.companyName} code from Bitbucket?`);
    if (!confirmed) return;

    try {
      setDeployingSiteId(site.id);
      const response = await api.post(`/sites/${site.id}/deploy`);
      showNotification(`Deploy queued: ${response.data.data?.commandId || 'SSM command started'}`, 'success');
      await loadSites();
    } catch (error) {
      console.error('Error deploying site:', error);
      showNotification(error.response?.data?.message || 'Error deploying site', 'error');
    } finally {
      setDeployingSiteId(null);
    }
  };

  const handleDeployActiveSites = async () => {
    const confirmed = window.confirm('Deploy latest code to all active tenant sites?');
    if (!confirmed) return;

    try {
      setDeployingAll(true);
      const response = await api.post('/sites/deploy-active');
      const results = response.data.data || [];
      const succeeded = results.filter((result) => result.success).length;
      const failed = results.length - succeeded;
      showNotification(`Deploy queued for ${succeeded} site${succeeded === 1 ? '' : 's'}${failed ? `, ${failed} failed to queue` : ''}`, failed ? 'warning' : 'success');
      await loadSites();
    } catch (error) {
      console.error('Error deploying active sites:', error);
      showNotification(error.response?.data?.message || 'Error deploying active sites', 'error');
    } finally {
      setDeployingAll(false);
    }
  };

  if (!site?.isGlobal) {
    return (
      <Box className="entity-management-container">
        <Box className="toolbar-container">
          <Box className="selection-info" sx={{ pl: 0 }}>
            <Typography variant="h6" className="page-title" sx={{ pl: 0, fontWeight: 'bold' }}>
              Sites
            </Typography>
          </Box>
        </Box>
        <Alert severity="warning" sx={{ mt: 2 }}>
          Site registry is only available on the main admin site.
        </Alert>
      </Box>
    );
  }

  const formatSetupOwner = (site) => {
    if (!site.setupOwner) return '';
    return `${site.setupOwner.firstName} ${site.setupOwner.lastName}`;
  };

  const formatDateTime = (value) => (
    value ? new Date(value).toLocaleString() : '-'
  );

  const renderDetailRow = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
        {value || '-'}
      </Typography>
    </Grid>
  );

  const columns = [
    { field: 'companyName', headerName: 'Company', flex: 1.2 },
    { field: 'slug', headerName: 'Subdomain', flex: 0.9 },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          label={formatStatus(params.value)}
          size="small"
          color={statusColors[params.value] || 'default'}
          variant={params.value === 'draft' || params.value === 'archived' ? 'outlined' : 'filled'}
        />
      )
    },
    {
      field: 'setupOwner',
      headerName: 'Setup Owner',
      flex: 0.9,
      valueGetter: (params) => formatSetupOwner(params.row)
    },
    {
      field: 'initialAdmin',
      headerName: 'Initial Admin',
      flex: 1,
      renderCell: (params) => {
        const initialAdmin = params.row.metadata?.initialAdmin || params.row.metadata?.initialAdminConfig;
        if (!initialAdmin?.email) return '';

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography variant="body2" noWrap title={initialAdmin.email}>
              {initialAdmin.email}
            </Typography>
            {initialAdmin.inviteStatus && (
              <Chip
                label={formatStatus(initialAdmin.inviteStatus)}
                size="small"
                color={initialAdmin.inviteStatus === 'queued' ? 'warning' : 'success'}
                sx={{ height: 20 }}
              />
            )}
          </Box>
        );
      }
    },
    {
      field: 'publicIp',
      headerName: 'Public IP',
      flex: 0.8,
      valueGetter: (params) => params.row.publicIp || ''
    },
    {
      field: 'ec2State',
      headerName: 'EC2 State',
      flex: 0.7,
      valueGetter: (params) => params.row.metadata?.ec2State || ''
    },
    {
      field: 'tenantUrl',
      headerName: 'Tenant URL',
      flex: 1,
      renderCell: (params) => params.value ? (
        <a href={params.value} target="_blank" rel="noreferrer">
          {params.value}
        </a>
      ) : ''
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      disableClickEventBubbling: true,
      renderCell: (params) => {
        if (!params.row) return null;

        return (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', px: 0 }}>
            <IconButton
              size="small"
              onClick={() => handleOpenDetails(params.row)}
              title="View Provisioning Details"
            >
              <DetailsIcon sx={{ width: 18, height: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleRefreshSiteEc2(params.row)}
              title="Refresh EC2 Status"
              disabled={!params.row.ec2InstanceId || refreshingSiteId === params.row.id}
            >
              <RefreshIcon sx={{ width: 18, height: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleOpenTenantUrl(params.row.tenantUrl)}
              title="Open Tenant Site"
              disabled={!params.row.tenantUrl}
            >
              <OpenIcon sx={{ width: 18, height: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={(event) => handleOpenActionMenu(event, params.row)}
              title="More Actions"
            >
              <MoreIcon sx={{ width: 18, height: 18 }} />
            </IconButton>
          </Box>
        );
      }
    }
  ];

  const statusOptions = editMode
    ? editStatusOptions.includes(currentSite.status)
      ? editStatusOptions
      : [currentSite.status, ...editStatusOptions]
    : createStatusOptions;

  return (
    <Box className="entity-management-container">
      <Box className="toolbar-container">
        <Box className="selection-info" sx={{ pl: 0 }}>
          <Typography variant="h6" className="page-title" sx={{ pl: 0, fontWeight: 'bold' }}>
            Sites
          </Typography>
        </Box>
        <Box className="button-group">
          <Button
            className="action-button"
            startIcon={<RefreshIcon />}
            onClick={handleRefreshEc2}
            disabled={refreshingEc2}
            sx={{
              textTransform: 'uppercase',
              fontWeight: 700,
              minWidth: 110,
              width: 'fit-content',
              padding: '6px 16px',
              border: '2px solid var(--color-primary)',
              color: 'var(--color-black)',
              '&:hover': {
                backgroundColor: 'var(--color-dark)',
                color: 'var(--color-white)',
                borderColor: 'var(--color-dark)'
              },
              '& .MuiButton-startIcon': {
                color: 'var(--color-primary)',
              }
            }}
          >
            Refresh
          </Button>
          <Button
            className="action-button"
            startIcon={<DeployIcon />}
            onClick={handleDeployActiveSites}
            disabled={deployingAll}
            sx={{
              textTransform: 'uppercase',
              fontWeight: 700,
              minWidth: 120,
              width: 'fit-content',
              padding: '6px 16px',
              border: '2px solid var(--color-primary)',
              color: 'var(--color-black)',
              '&:hover': {
                backgroundColor: 'var(--color-dark)',
                color: 'var(--color-white)',
                borderColor: 'var(--color-dark)'
              },
              '& .MuiButton-startIcon': {
                color: 'var(--color-primary)',
              }
            }}
          >
            Deploy All
          </Button>
          <Button
            className="action-button"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{
              textTransform: 'uppercase',
              fontWeight: 700,
              minWidth: 90,
              width: 'fit-content',
              padding: '6px 16px',
              border: '2px solid var(--color-primary)',
              color: 'var(--color-black)',
              '&:hover': {
                backgroundColor: 'var(--color-dark)',
                color: 'var(--color-white)',
                borderColor: 'var(--color-dark)'
              },
              '& .MuiButton-startIcon': {
                color: 'var(--color-primary)',
              }
            }}
          >
            Add
          </Button>
        </Box>
      </Box>

      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {actionMenuSite && (
          <Box>
            {['draft', 'failed'].includes(actionMenuSite.status) && (
              <MenuItem
                onClick={() => handleActionMenuItem(handleProvision, actionMenuSite)}
                disabled={provisioningSiteId === actionMenuSite.id}
              >
                <ListItemIcon>
                  <ProvisionIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {actionMenuSite.status === 'failed' ? 'Retry Provisioning' : 'Provision Site'}
                </ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={() => handleActionMenuItem(handleEdit, actionMenuSite)}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit Site</ListItemText>
            </MenuItem>
            {actionMenuSite.status === 'active' &&
              actionMenuSite.ec2InstanceId &&
              actionMenuSite.metadata?.ec2State === 'running' && (
                <MenuItem
                  onClick={() => handleActionMenuItem(handleDeploySite, actionMenuSite)}
                  disabled={deployingSiteId === actionMenuSite.id}
                >
                  <ListItemIcon>
                    <DeployIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Deploy Site</ListItemText>
                </MenuItem>
              )}
            {actionMenuSite.ec2InstanceId && actionMenuSite.metadata?.ec2State === 'running' && (
              <MenuItem
                onClick={() => handleActionMenuItem(handleStopEc2, actionMenuSite)}
                disabled={stoppingSiteId === actionMenuSite.id}
              >
                <ListItemIcon>
                  <StopIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Stop EC2 Instance</ListItemText>
              </MenuItem>
            )}
            {actionMenuSite.ec2InstanceId && actionMenuSite.metadata?.ec2State === 'stopped' && (
              <MenuItem
                onClick={() => handleActionMenuItem(handleStartEc2, actionMenuSite)}
                disabled={startingSiteId === actionMenuSite.id}
              >
                <ListItemIcon>
                  <StartIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Start EC2 Instance</ListItemText>
              </MenuItem>
            )}
            {actionMenuSite.status === 'archived' && <Divider />}
            {actionMenuSite.ec2InstanceId &&
              actionMenuSite.status === 'archived' &&
              !['shutting-down', 'terminated'].includes(actionMenuSite.metadata?.ec2State) && (
                <MenuItem
                  onClick={() => handleActionMenuItem(handleTerminateEc2, actionMenuSite)}
                  disabled={terminatingSiteId === actionMenuSite.id}
                >
                  <ListItemIcon>
                    <TerminateIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText>Terminate EC2 Instance</ListItemText>
                </MenuItem>
              )}
            {actionMenuSite.status === 'archived' &&
              (!actionMenuSite.ec2InstanceId ||
                ['stopped', 'shutting-down', 'terminated'].includes(actionMenuSite.metadata?.ec2State)) && (
                <MenuItem
                  onClick={() => handleActionMenuItem(handleDeleteArchivedSite, actionMenuSite)}
                  disabled={deletingSiteId === actionMenuSite.id}
                >
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText>Delete Archived Site Row</ListItemText>
                </MenuItem>
              )}
          </Box>
        )}
      </Menu>

      <Box className="main-content">
        <Box className="table-container">
          <SearchableDataTable
            searchPlaceholder="Search Sites..."
            rows={sites}
            columns={columns}
            loading={loading}
            tableName="sites"
            height="100%"
          />
        </Box>
      </Box>

      <Dialog open={openDialog} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Edit Site' : 'Add Site'}</DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Company Name"
                name="companyName"
                value={currentSite.companyName}
                onChange={handleCompanyNameChange}
                fullWidth
                required
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Subdomain"
                name="slug"
                value={currentSite.slug}
                onChange={handleChange}
                fullWidth
                required
                autoComplete="off"
                helperText="Lowercase letters, numbers, and hyphens"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Status"
                name="status"
                value={currentSite.status}
                onChange={handleChange}
                fullWidth
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {formatStatusOption(status)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Setup Owner"
                name="setupOwnerUserId"
                value={currentSite.setupOwnerUserId}
                onChange={handleChange}
                fullWidth
              >
                <MenuItem value="">Unassigned</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.firstName} {user.lastName} ({user.email})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Initial Admin
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="First Name"
                name="initialAdminFirstName"
                value={currentSite.initialAdminFirstName}
                onChange={handleChange}
                fullWidth
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Last Name"
                name="initialAdminLastName"
                value={currentSite.initialAdminLastName}
                onChange={handleChange}
                fullWidth
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Email"
                name="initialAdminEmail"
                value={currentSite.initialAdminEmail}
                onChange={handleChange}
                fullWidth
                required={currentSite.status === 'provisioning'}
                autoComplete="off"
                helperText="Tenant admin login email"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                name="notes"
                value={currentSite.notes}
                onChange={handleChange}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editMode ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle>Site Details</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          {detailsLoading ? (
            <Typography variant="body2">Loading...</Typography>
          ) : siteDetails ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  Site
                </Typography>
                <Grid container spacing={2}>
                  {renderDetailRow('Company', siteDetails.site?.companyName)}
                  {renderDetailRow('Status', formatStatus(siteDetails.site?.status))}
                  {renderDetailRow('Subdomain', siteDetails.site?.slug)}
                  {renderDetailRow('Tenant URL', siteDetails.site?.tenantUrl)}
                  {renderDetailRow('Instance ID', siteDetails.site?.ec2InstanceId)}
                  {renderDetailRow('AWS Region', siteDetails.site?.awsRegion)}
                  {renderDetailRow('Public IP', siteDetails.site?.publicIp)}
                  {renderDetailRow('Setup Owner', formatSetupOwner(siteDetails.site))}
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  Health
                </Typography>
                <Grid container spacing={2}>
                  {renderDetailRow('Last Health URL', siteDetails.site?.metadata?.lastHealthCheckUrl)}
                  {renderDetailRow(
                    'Last Health OK',
                    siteDetails.site?.metadata?.lastHealthCheckOk === undefined
                      ? '-'
                      : String(siteDetails.site.metadata.lastHealthCheckOk)
                  )}
                  {renderDetailRow('Last Health Check', formatDateTime(siteDetails.site?.metadata?.lastHealthCheckAt))}
                  {renderDetailRow('Last Error', siteDetails.site?.lastProvisioningError)}
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  EC2
                </Typography>
                <Grid container spacing={2}>
                  {renderDetailRow('State', siteDetails.ec2?.state || siteDetails.site?.metadata?.ec2State)}
                  {renderDetailRow('Instance Type', siteDetails.ec2?.instanceType)}
                  {renderDetailRow('Availability Zone', siteDetails.ec2?.availabilityZone)}
                  {renderDetailRow('Launch Time', formatDateTime(siteDetails.ec2?.launchTime))}
                  {renderDetailRow('Private IP', siteDetails.ec2?.privateIp)}
                  {renderDetailRow('Last EC2 Refresh', formatDateTime(siteDetails.site?.metadata?.lastEc2RefreshAt))}
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  Backup
                </Typography>
                <Grid container spacing={2}>
                  {renderDetailRow('Status', formatStatus(siteDetails.site?.metadata?.backup?.status))}
                  {renderDetailRow('Last Started', formatDateTime(siteDetails.site?.metadata?.backup?.lastStartedAt))}
                  {renderDetailRow('Last Finished', formatDateTime(siteDetails.site?.metadata?.backup?.lastFinishedAt))}
                  {renderDetailRow('DB Command ID', siteDetails.site?.metadata?.backup?.lastDbBackupCommandId)}
                  {renderDetailRow('AMI ID', siteDetails.site?.metadata?.backup?.lastAmiId)}
                  {renderDetailRow('AMI Name', siteDetails.site?.metadata?.backup?.lastAmiName)}
                  {renderDetailRow('DB Retention Days', siteDetails.site?.metadata?.backup?.dbRetentionDays)}
                  {renderDetailRow('AMI Retention Count', siteDetails.site?.metadata?.backup?.amiRetentionCount)}
                  {renderDetailRow('Last Error', siteDetails.site?.metadata?.backup?.lastError)}
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  Deployment
                </Typography>
                <Grid container spacing={2}>
                  {renderDetailRow('Command ID', siteDetails.deployment?.commandId || siteDetails.site?.metadata?.lastDeployCommandId)}
                  {renderDetailRow('Status', siteDetails.deployment?.status || siteDetails.site?.metadata?.lastDeployStatus)}
                  {renderDetailRow('Status Details', siteDetails.deployment?.statusDetails)}
                  {renderDetailRow('Branch', siteDetails.site?.metadata?.lastDeployBranch)}
                  {renderDetailRow('Requested', formatDateTime(siteDetails.deployment?.requestedAt || siteDetails.site?.metadata?.lastDeployRequestedAt))}
                  {renderDetailRow('Finished', formatDateTime(siteDetails.deployment?.finishedAt))}
                  {renderDetailRow('Response Code', siteDetails.deployment?.responseCode === undefined ? '-' : String(siteDetails.deployment.responseCode))}
                  {renderDetailRow('Deploy Error', siteDetails.deployment?.standardError)}
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  Provisioning Jobs
                </Typography>
                <Stack spacing={1}>
                  {(siteDetails.jobs || []).length ? siteDetails.jobs.map((job) => (
                    <Box key={job.id} sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                      <Grid container spacing={1}>
                        {renderDetailRow('Status', formatStatus(job.status))}
                        {renderDetailRow('Attempts', String(job.attempts))}
                        {renderDetailRow('Started', formatDateTime(job.startedAt))}
                        {renderDetailRow('Finished', formatDateTime(job.finishedAt))}
                        {renderDetailRow('Last Error', job.lastError)}
                      </Grid>
                    </Box>
                  )) : (
                    <Typography variant="body2">No provisioning jobs found.</Typography>
                  )}
                </Stack>
              </Box>
            </Stack>
          ) : (
            <Typography variant="body2">No details loaded.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SiteManagement;
